import React, { useCallback, useEffect, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { getVoiceAsync } from './mediaUtils';
import { Platform, createUseStyles, usePostChatUploadFile } from 'core/src';
import { Store, setDocument, setUploadedFile } from '../store';
import { useToast } from 'react-native-toast-notifications';
import { useTranslation } from 'react-i18next';

const MaterialIconsX = MaterialIcons as unknown as React.ComponentType<any>;

interface Props {
  isTyping: () => void;
  onSend: (e: any) => void;
  conversationId: string;
  setEmojiKeyboardVisible: any;
  generateUniqueRandomNumber: any;
  onSendFile: any;
}

const AccessoryBar: React.FC<Props> = ({
  isTyping,
  onSend,
  conversationId,
  setEmojiKeyboardVisible,
  generateUniqueRandomNumber,
  onSendFile,
}) => {
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [lenght, setLenght] = useState<any>(null);

  const dispatch = Store.useDispatch();
  const state = Store.useState();

  const { mutate } = usePostChatUploadFile({
    onSuccess(data) {
      dispatch(setUploadedFile(data));
    },
    onError() {
      toast.show(i18n.t('notSupportFile'), {
        type: 'danger',
        placement: 'top',
        animationType: 'zoom-in',
      });
    },
  });

  useEffect(() => {
    if (state.uploadedFile && lenght?.length > 1) {
      onSendFile();
    }
  }, [state.uploadedFile, lenght]);

  const { styles, colors } = useStyles();

  const handleSend = async (e: any) => {
    if (e.isRecording) {
      setIsRecording(e.isRecording);
    }
    if (e.result) {
      const _data = e.result;
      const url = URL.createObjectURL(e.result);
      const file = await processFile(_data);

      dispatch(setDocument({ assets: e.result, audio: url }), () => {
        if (file) {
          mutate({
            //@ts-ignore
            requestBody: {
              file: file as unknown as string,
              conversationId,
            },
          });
        }
      });
    } else if (!e.isRecording && !e.result) {
      onSend(e);
    }
  };

  const handleImagePicker = useCallback(async () => {
    const fileInput = document.getElementById('file');
    fileInput?.click();
  }, []);

  const handleChangeFile = async (e: any) => {
    const files = e.target.files;
    if (!files.length) return;

    setLenght(Array.from(files));

    const uploadFile = async (file: File) => {
      const url = await URL.createObjectURL(file);

      dispatch(
        setDocument({
          assets: files,
          uri: url,
        }),
        () => {
          mutate({
            requestBody: {
              file: file as unknown as string,
              conversationId,
            },
          });
        },
      );
    };

    if (files.length > 1) {
      for (let i = 0; i < files.length; i++) {
        setTimeout(() => {
          uploadFile(files[i]);
        }, i * 2000);
      }
    } else {
      uploadFile(files[0]);
    }
  };

  const openEmojiKeyboard = () => {
    setEmojiKeyboardVisible(true);
  };

  const [recordingDuration, setRecordingDuration] = useState(0);
  useEffect(() => {
    let interval: any;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingDuration((prevDuration) => prevDuration + 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }

    return () => {
      clearInterval(interval);
      setRecordingDuration(0);
    };
  }, [isRecording]);

  const formatTime = (recordingDuration: any) => {
    const mins = Math.floor(recordingDuration / 60);
    const secs = recordingDuration % 60;
    return `${mins < 10 ? '0' : ''}${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const toast = useToast();
  const { i18n } = useTranslation();
  const _toast = (err: any) => {
    toast.show(
      err.includes('device')
        ? i18n.t('deviceNotFound')
        : err.includes('Permission')
          ? i18n.t('PermissionDenied')
          : '',
      { type: 'danger' },
    );
  };

  useEffect(() => {
    const handleDrop = async (e: DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer?.files[0];
      if (file) {
        const url = await URL.createObjectURL(file);
        if (url) {
          dispatch(
            setDocument({
              assets: e.dataTransfer?.files as FileList,
              uri: url,
            }),
            () => {
              mutate({
                //@ts-ignore
                requestBody: {
                  file: file as unknown as string,
                  conversationId,
                },
              });
            },
          );
        }
      }
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
    };

    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('drop', handleDrop);

    return () => {
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('drop', handleDrop);
    };
  }, [dispatch, conversationId, mutate]);

  useEffect(() => {
    const handlePaste = async (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (items) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.kind === 'file' && item.type.startsWith('image/')) {
            const file = item.getAsFile();
            if (file) {
              const dataTransfer = new DataTransfer();
              dataTransfer.items.add(file);

              const url = URL.createObjectURL(file);
              dispatch(
                setDocument({
                  assets: dataTransfer.files,
                  uri: url,
                }),
                () => {
                  mutate({
                    //@ts-ignore
                    requestBody: {
                      file: file as unknown as string,
                      conversationId,
                    },
                  });
                },
              );
            }
          }
        }
      }
    };
    //@ts-ignore
    window.addEventListener('paste', handlePaste);

    return () => {
      //@ts-ignore
      window.removeEventListener('paste', handlePaste);
    };
  }, [dispatch, conversationId, mutate]);

  return (
    <View style={styles.container}>
      <Button onPress={handleImagePicker} name="attachment" />
      {Platform.isDesktop && (
        <Button onPress={openEmojiKeyboard} name="emoji-emotions" />
      )}
      <View style={{ flexDirection: 'row' }}>
        <Button
          onPress={() => {
            setIsRecording((prev) => !prev);
            getVoiceAsync(handleSend, isRecording, setIsRecording, _toast);
          }}
          name="mic"
          classes={{ color: isRecording ? 'red' : colors.secondryColor }}
        ></Button>
        {isRecording ? (
          <Text style={styles.time}>{formatTime(recordingDuration)}</Text>
        ) : null}
      </View>
      <input
        id="file"
        type="file"
        max={10}
        multiple={true}
        hidden
        onChange={handleChangeFile}
      />
    </View>
  );
};

interface ButtonProps {
  onPress: () => void;
  size?: number;
  name: string;
  classes?: any;
  children?: any;
}

const Button: React.FC<ButtonProps> = ({
  onPress,
  size = 30,
  name,
  classes,
  children,
}) => {
  const { colors } = useStyles();
  return (
    <TouchableOpacity onPress={onPress}>
      <MaterialIconsX
        name={name}
        size={size}
        color={colors.secondryColor}
        style={classes}
      />
      {children}
    </TouchableOpacity>
  );
};

const useStyles = createUseStyles(({ colors, theme }) => ({
  container: {
    height: 44,
    width: '100%',
    backgroundColor: colors.inputTollbar,
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: 'rgba(0,0,0,0.3)',
  },
  time: { paddingTop: 3, paddingLeft: 10 },
}));

async function processFile(file: File): Promise<File | undefined> {
  const AudioContext =
    window.AudioContext || (window as any).webkitAudioContext;
  if (file.type === 'audio/wav') {
    const audioData = await file.arrayBuffer();
    const audioContext = new AudioContext();
    const audioBuffer = await audioContext.decodeAudioData(audioData);

    if (
      audioBuffer.sampleRate === 8000 &&
      audioBuffer.numberOfChannels === 1 &&
      file.name.endsWith('.wav')
    ) {
      return file; // The file is already in the correct format, so return it as is
    }
  }

  if (!file?.arrayBuffer()) return;
  const arrayBuffer = await file?.arrayBuffer?.();
  const audioContext = new AudioContext();
  const audioBuffer = await audioContext?.decodeAudioData(arrayBuffer);
  if (
    file.type !== 'audio/wav' ||
    audioBuffer.sampleRate !== 8000 ||
    audioBuffer.numberOfChannels !== 1
  ) {
    const monoAudioBuffer = await convertToMonoAt8kHz(
      audioBuffer,
      audioContext,
    );
    const pcmData = await convertTo16BitPCM(monoAudioBuffer);
    const wavBlob = await encodeWAV(pcmData, monoAudioBuffer.sampleRate);
    const processedFile = new File(
      [wavBlob],
      file?.name?.replace(/\.[^/.]+$/, '.wav') || `${Math.random()}.wav`,
      { type: 'audio/wav' },
    );
    return processedFile;
  }
  return file;
}

async function convertToMonoAt8kHz(
  audioBuffer: AudioBuffer,
  audioContext: AudioContext,
) {
  const targetSampleRate = 8000;
  const channelData = audioBuffer.getChannelData(0);
  const newBufferLength = Math.floor(
    (channelData.length * targetSampleRate) / audioBuffer.sampleRate,
  );
  const offlineContext = new OfflineAudioContext(
    1,
    newBufferLength,
    targetSampleRate,
  );
  const bufferSource = offlineContext.createBufferSource();
  bufferSource.buffer = audioBuffer;
  bufferSource.connect(offlineContext.destination);
  bufferSource.start(0);
  return offlineContext.startRendering();
}

function convertTo16BitPCM(audioBuffer: AudioBuffer): Int16Array {
  const output = new Int16Array(audioBuffer.length);
  const inputData = audioBuffer.getChannelData(0);

  for (let i = 0; i < inputData.length; i++) {
    const sample = Math.max(-1, Math.min(1, inputData[i])); // Clamp the sample to [-1, 1]
    output[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff; // Convert to 16-bit
  }

  return output;
}

function encodeWAV(pcmData: Int16Array, sampleRate: number): Blob {
  const buffer = new ArrayBuffer(44 + pcmData.length * 2);
  const view = new DataView(buffer);
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + pcmData.length * 2, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true); // Mono
  view.setUint32(24, 8000, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, 'data');
  view.setUint32(40, pcmData.length * 2, true);

  let offset = 44;
  for (let i = 0; i < pcmData.length; i++, offset += 2) {
    view.setInt16(offset, pcmData[i], true);
  }

  return new Blob([view], { type: 'audio/wav' });
}

function writeString(view: DataView, offset: number, string: string) {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}

export default AccessoryBar;
